import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Gift, Calendar, Sparkles } from "lucide-react";

const TIER_COLORS = {
  A: "bg-purple-50 border-purple-200 text-purple-700",
  B: "bg-blue-50 border-blue-200 text-blue-700",
  C: "bg-emerald-50 border-emerald-200 text-emerald-700",
  D: "bg-amber-50 border-amber-200 text-amber-800",
};

const fmtINR = (n) => (n ? "₹" + Number(n).toLocaleString("en-IN") : "—");

export default function MyFestivalHistory() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/my-portal/festivals/history")
      .then(({ data }) => setHistory(data || []))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  const totalReceived = history.reduce((s, h) => s + (h.total_value || 0), 0);

  return (
    <div className="space-y-4" data-testid="my-festival-history">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
          <Gift className="h-3 w-3" /> My Portal
        </div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          🎁 My Festival Gifts
        </h1>
        <p className="text-xs text-slate-500 mt-0.5">Track all festival gifts and bonuses you've received from Bravura.</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-red-700">Total Gifts Received</div>
          <div className="text-2xl font-bold text-red-700" style={{ fontFamily: "Chivo" }} data-testid="total-count">{history.length}</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700">Total Value</div>
          <div className="text-2xl font-bold text-emerald-700" style={{ fontFamily: "Chivo" }} data-testid="total-value">{fmtINR(totalReceived)}</div>
        </div>
      </div>

      {history.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-400">
          <Gift className="h-10 w-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No festival gifts on record yet. They'll appear here as HR distributes them.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {history.map((h) => (
            <div key={h.id} className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-3" data-testid={`gift-${h.id}`}>
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-slate-900 flex items-center gap-2">
                  {h.festival_name} <span className="text-xs text-slate-500 font-normal">{h.festival_year}</span>
                  <span className={`inline-block px-1.5 py-0.5 rounded-full text-[9px] font-bold ${TIER_COLORS[h.tier]}`}>
                    Tier {h.tier}
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                  <Calendar className="h-3 w-3" /> {h.festival_date || "—"}
                </div>
                <div className="text-xs text-slate-600 mt-1">
                  {h.cash_amount > 0 && <span>Cash: <b className="text-slate-900">{fmtINR(h.cash_amount)}</b></span>}
                  {h.voucher_amount > 0 && <span className="ml-2">Voucher ({h.voucher_brand || "—"}): <b className="text-slate-900">{fmtINR(h.voucher_amount)}</b></span>}
                  {h.physical_gift && <span className="ml-2 text-red-700">+ {h.physical_gift}</span>}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Total</div>
                <div className="text-lg font-bold text-emerald-700" style={{ fontFamily: "Chivo" }}>{fmtINR(h.total_value)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
